import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Divider from '@base/Divider/Divider';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Image from '@base/Image';
import CardCarousel from '@cont/PreSubscribed/CardCarousel';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useScholarshipTestInfo from '@api/hooks/scholarship/useScholarshipTestInfo';

const Wrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-bottom: 110px;
  text-align: center;
`;

const StyledH2 = styled(H2)`
  max-width: 35%;
  margin: ${Spacings.SPACING_6B} auto ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    max-width: 75%;
  }
`;

const StyledP1 = styled(P1)`
  font-size: 20px;
  margin-bottom: ${Spacings.SPACING_14B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    max-width: 90%;
    margin: auto;
    margin-bottom: ${Spacings.SPACING_14B};
  }
`;

const TopperCardWrapper = styled.div`
  width: 350px;
  padding: ${Spacings.SPACING_8B};
  background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.21),
      rgba(255, 255, 255, 0.21)
    ),
    var(--color-base-1);
  box-shadow: ${boxShadowGenerator({ color: 'rgba(11, 11, 11, 0.05)' })};
  border-radius: 8px;
  margin-right: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Thumbnail = styled.div`
  height: 160px;
  width: 160px;
  margin: auto;
  position: relative;
  margin-bottom: ${Spacings.SPACING_9B};
`;

const RankText = styled.div`
  position: absolute;
  bottom: -8px;
  left: 20%;
  background: ${({ $bgColor }) => $bgColor};
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_4B};
  border-radius: 40px;
  border: 6px solid var(--color-base-1);
`;

const Rank = styled(H5)``;

const SubText = styled(P1)`
  font-size: ${Fonts.LARGE};
  margin-top: ${Spacings.SPACING_1B};
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_8B} auto;
`;

const PrizeWon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Logo = styled.img`
  height: 24px;
  width: 24px;
  margin-right: ${Spacings.SPACING_3B};
`;

const colorsMapping = {
  gold: {
    bg: 'var(--color-i-yellow-2)',
    text: 'var(--color-i-brown-5)'
  },
  silver: {
    bg: 'var(--color-i-gray-11)',
    text: 'var(--color-i-black-7)'
  },
  bronze: {
    bg: 'var(--color-i-orange-12)',
    text: 'var(--color-i-brown-4)'
  }
};

const Topper = () => {
  const {
    query: { eventKey }
  } = useRouter();
  const { prodigyInfo } = useScholarshipTestInfo(eventKey);

  const { toppersMeta, toppers } = prodigyInfo;

  if (!toppers || !toppers.length) return null;

  return (
    <Wrapper>
      {toppersMeta && (
        <>
          <Image src={toppersMeta.image} alt="Toppers" width={96} height={96} />
          <StyledH2>{toppersMeta.title}</StyledH2>
          <StyledP1>{toppersMeta.subTitle}</StyledP1>
        </>
      )}
      <CardCarousel type="dummy">
        {toppers.map((topper) => {
          let rankColors = colorsMapping.bronze;
          if (topper.rank === 1) {
            rankColors = colorsMapping.gold;
          } else if (topper.rank >= 2 && topper.rank <= 5) {
            rankColors = colorsMapping.silver;
          }
          return (
            <TopperCardWrapper key={topper.id}>
              <div>
                <Thumbnail>
                  <Image
                    src={topper.thumbnail}
                    height={160}
                    width={160}
                    alt="toppr thumbnail"
                  />
                  <RankText $bgColor={rankColors.bg}>
                    <Rank color={rankColors.text}>{`Rank ${topper.rank}`}</Rank>
                  </RankText>
                </Thumbnail>
                <H2>{topper.name}</H2>
                <SubText>{topper.subText1}</SubText>
                <SubText color="var(--color-text-primary)">
                  {topper.subText2}
                </SubText>
              </div>
              <div>
                <StyledDivider />
                <PrizeWon>
                  <Logo src={topper.prizeWon.itemLogo} />
                  <H5>{topper.prizeWon.itemName}</H5>
                </PrizeWon>
              </div>
            </TopperCardWrapper>
          );
        })}
      </CardCarousel>
    </Wrapper>
  );
};

export default Topper;
